//#region IMPORTS
import { Fragment, ReactNode } from "react";
import { motion } from "framer-motion";
//#endregion

//#region INTERFACE
interface Props {
  message?: string;
  children?: ReactNode;
}
//#endregion

export default function LoadingOverlay({
  message = "Loading…",
  children,
}: Props) {
  //#region SETUP
  const variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 0.6,
    },
  };
  //#endregion

  //#region RENDER
  return (
    <Fragment>
      <motion.div
        variants={variants}
        initial="hidden"
        animate="visible"
        className="fixed bg-white left-0 h-screen w-screen z-20"
      >
        {children}
      </motion.div>

      {/* Sibling of the veil, not a child: the veil animates to opacity 0.6, so
          anything nested inside it fades out too. Without this badge a slow
          fetch just washed the page out with no sign of activity, which reads
          as "the portal is frozen". Delayed so quick fetches don't flash it.
          pointer-events-none: the filter bar sits above the veil (z-40) and
          stays usable while a fetch is in flight. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="fixed inset-0 z-30 flex items-center justify-center pointer-events-none"
      >
        <span className="inline-flex items-center gap-3 rounded-full bg-white px-5 py-2.5 shadow-lg ring-1 ring-inset ring-gray-900/10">
          <svg
            className="animate-spin h-5 w-5 text-purple-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="text-sm font-medium text-gray-700">{message}</span>
        </span>
      </motion.div>
    </Fragment>
  );
  //#endregion
}
